import { Component, Input, OnInit } from '@angular/core';
import {
	ApplicationConstants,
	constants,
	EnumUtilityService,
	GeneralContentService,
	LocalStorageService,
	LodashUtilityService,
	MappedContentItem,
	NOTIFICATION_ICON_NAMES,
	NotificationAlertIndicatorService,
	NotificationFilterTypes,
	SortUtilityService,
	UserTypeService
} from '@edassist/common';
import {
	AnnouncementStatus,
	Application,
	AuthenticationService,
	ContentItem,
	ContentOptions,
	ContentService,
	Notification,
	NOTIFICATION_EVENT_TYPE,
	NotificationAccess,
	NotificationService,
	ParticipantService
} from '@edassist/api';
import { SliderDirection } from '@edassist-ui/slider';
import { HEADING_SIZES, HEADING_TYPES } from '@edassist-ui/heading';
import { finalize, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { LoadingContainerService } from '@edassist/shared';
import {
	NOTIFICATION_ITEM_TYPE,
	NotificationItem
} from './notification-item.interface';
import { InputDropdownOption } from '@edassist-ui/input-dropdown';

@Component({
	selector: 'ea-notifications',
	templateUrl: './notifications.component.html',
	styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {
	constants = constants;
	notifications: Array<Notification> = [];
	bulkAppReviewCount = 0;
	announcementContents: Array<MappedContentItem> = [];
	SliderDirection = SliderDirection;
	HEADING_SIZES = HEADING_SIZES;
	HEADING_TYPES = HEADING_TYPES;
	isLoading = false;
	hasNotifications = false;
	isAdminNotification = false;
	@Input() clientParticipantId: number;
	@Input() isClientAdmin: boolean;
	announcementStatusMap = new Map();
	notificationItems: Array<NotificationItem> = [];
	originalNotificationItems: Array<NotificationItem> = [];
	NOTIFICATION_ITEM_TYPE = NOTIFICATION_ITEM_TYPE;
	selectedNotificationFilterType: NotificationFilterTypes =
		NotificationFilterTypes.ALL_TYPES;
	notificationTypeValue: string;
	notificationTypeOptions: Array<InputDropdownOption> = [];
	showFilter = false;
	showBulkReview = true;
	hasSelectedTypeValue = true;
	selectedNotificationFilterIcon: NOTIFICATION_ICON_NAMES =
		NOTIFICATION_ICON_NAMES.ALL_TYPES;

	protected continuationToken = '';
	protected pageNumber = 0;
	protected isNotificationPageAvailable = true;
	protected clientId: number;
	protected participantId: number;
	private readonly pageSize = constants.TUITION.NOTIFICATION.recordsPerPage;
	private isAdminOrSupervisor = false;
	private isSupervisor;
	private isAdminAndSupervisor;
	private isInitialLoad = true;
	private announcementUnReadCount = 0;
	private notificationAccess: NotificationAccess;
	private readonly notificationsMap = new Map();

	constructor(
		protected readonly authenticationService: AuthenticationService,
		protected readonly notificationService: NotificationService,
		protected readonly participantService: ParticipantService,
		protected readonly contentService: ContentService,
		protected readonly notificationAlertIndicatorService: NotificationAlertIndicatorService,
		protected readonly loadingService: LoadingContainerService
	) {}

	ngOnInit(): void {
		this.notificationAlertIndicatorService.notificationAccessedEvent
			.pipe(
				switchMap((notificationAccess) => {
					if (!notificationAccess || !notificationAccess.notificationLastReadDate) {
						return this.notificationService.getNotificationAccess(this.participantId);
					}

					return of(notificationAccess);
				})
			)
			.subscribe((notificationAccess) => {
				this.notificationAccess = notificationAccess;
				this.isAnnouncementsRead();
			});
		this.loadingService.displayLoaderById(constants.loaderIds.NOTIFICATIONS);
		this.participantId =
			this.clientParticipantId ?
				this.clientParticipantId
			:	this.authenticationService.currentUserValue?.participantId;

		const loggedInUserType =
			this.authenticationService.currentUserValue?.userTypeId;
		this.isSupervisor = UserTypeService.isSupervisor(loggedInUserType);


		this.isAdminOrSupervisor =
			UserTypeService.isSupervisor(loggedInUserType) ||
			UserTypeService.isClientAdmin(loggedInUserType);
		this.isAdminNotification =
			(UserTypeService.isClientAdmin(loggedInUserType) &&
				!!this.clientParticipantId) ||
			UserTypeService.isInternalAdmin(loggedInUserType);
		forkJoin([
			this.participantService.isClientAdminAndSupervisor(this.participantId),
			this.participantService.getParticipant(this.participantId)
		]).subscribe(([clientAdminAndSupervisor, participant]) => {
			this.isAdminAndSupervisor = clientAdminAndSupervisor;
			this.clientId = participant.clientId;
			this.getNotifications();
			this.populateNotificationTypeOptions();
		});
	}

	getNotifications(): void {
		const contentOptions: ContentOptions = {
			client: this.clientId?.toString(),
			textOnly: false
		};

		if (this.isNotificationPageAvailable) {
			this.isLoading = true;
		}

		if (!this.notificationAccess) {
			// if accessed from old angular
			this.notificationAccess = LocalStorageService.getItem(
				constants.localStorageKeys.NOTIFICATIONS_ACCESSED
			)?.notificationAccess;
		}
		forkJoin([
			this.isNotificationPageAvailable ?
				this.notificationService.getParticipantNotificationEvents(
					this.participantId,
					this.pageNumber,
					this.pageSize,
					this.continuationToken
				)
			:	of(undefined),
			(
				this.isInitialLoad &&
				this.isAdminOrSupervisor &&
				!this.clientParticipantId
			) ?
				this.participantService.getBulkReviewApplications(
					this.participantId,
					ApplicationConstants.SUPERVISOR_REVIEW_APPS_STATUS
				)
			:	of(undefined),
			(
				this.isInitialLoad &&
				this.isAdminOrSupervisor &&
				!this.clientParticipantId
			) ?
				this.participantService.getReviewApplications(
					this.participantId,
					ApplicationConstants.SUPERVISOR_REVIEW_APPS_STATUS
				)
			:	of(undefined),
			this.isInitialLoad ?
				this.contentService.findByComponent(
					this.constants.contentConstants.contentTypes.general,
					this.constants.contentConstants.CONTENT_COMPONENTS.MESSAGE,
					contentOptions
				)
			:	of(undefined),
			this.notificationService.getAnnouncementsStatuses(this.participantId)
		])
			.pipe(
				finalize(() => {
					this.isLoading = false;
					if (this.isInitialLoad) {
						this.loadingService.hideLoaderById(
							constants.loaderIds.NOTIFICATIONS
						);
						this.isInitialLoad = false;
					}
				})
			)
			.subscribe(
				([
					notificationResult,
					bulkApplications,
					regularApplications,
					announcements,
					announcementsStatuses
				]) => {
					if (notificationResult?.result?.length) {
						this.notifications = this.notifications.concat(
							notificationResult?.result?.map((notification) =>
								this.setEventsFlag(notification)
							)
						);
						this.notifications = LodashUtilityService.uniqBy(
							this.notifications,
							'id'
						);
						this.notifications.forEach((notification, index) =>
							this.notificationsMap.set(notification.id, index)
						);
						this.continuationToken = notificationResult?.token;
						// if the token is null then no more pages to retrieve
						if (!this.continuationToken) {
							this.isNotificationPageAvailable = false;
						} else {
							this.pageNumber++;
						}
					}

					if (
						bulkApplications?.applications ||
						regularApplications?.applications
					) {
						this.bulkAppReviewCount = this.getUniqueReviewApplicationsCount(
							bulkApplications?.applications,
							regularApplications?.applications
						);
					}

					this.initAnnouncementStatusMap(announcementsStatuses);
					this.extractAnnouncementContent(announcements);
					this.mergeAnnouncementsAndNotifications();
					if (
						this.notifications?.length ||
						this.bulkAppReviewCount ||
						this.announcementContents?.length
					) {
						this.hasNotifications = true;
						this.showFilter = true;
						const announcementAndAdminMsgCount =
							this.announcementUnReadCount + (this.bulkAppReviewCount ? 1 : 0);
						this.notificationAlertIndicatorService.notifyAnnouncementAndAdminMsgCount(
							announcementAndAdminMsgCount
						);
					}
				}
			);
	}

	onScrollDown(): void {
		this.getNotifications();
	}

	getUniqueReviewApplicationsCount(
		bulkApplications: Array<Application>,
		regularApplications: Array<Application>
	): number {
		const bulkAppIds =
			bulkApplications ?
				new Set(bulkApplications.map((application) => application.id))
			:	new Set();

		return [
			...bulkApplications,
			...regularApplications?.filter(
				(regularApp) =>
					regularApp.participantId !== this.participantId &&
					!bulkAppIds.has(regularApp.id)
			)
		]?.length;
	}

	markAllAsRead(): void {
		if (!this.isAdminNotification) {
			this.markAllNotificationAsRead();
			this.markAllAnnouncementsAsRead();
			this.announcementUnReadCount = 0;
		}
	}

	markAnnouncementAsRead(announcementId: string): void {
		let announcementStatus = this.announcementStatusMap.get(announcementId);
		if (!this.isAdminNotification && !announcementStatus?.read) {
			announcementStatus =
				this.updateAnnouncementStatusMapAndNotify(announcementStatus);
			const announcementStatuses: Array<AnnouncementStatus> = [
				announcementStatus
			];
			this.updateAnnouncementStatuses(announcementStatuses);
		}
	}

	isAnnouncementRead(announcementId: string): boolean {
		return this.announcementStatusMap.get(announcementId) ?
				this.announcementStatusMap.get(announcementId)?.read
			:	false;
	}

	onNotificationTypeChange(
		selectedNotificationType: NotificationFilterTypes
	): void {
		this.selectedNotificationFilterType =
			NotificationFilterTypes[selectedNotificationType];
		this.notificationTypeValue = selectedNotificationType;
		this.selectedNotificationFilterIcon =
			NOTIFICATION_ICON_NAMES[selectedNotificationType];
		this.filterNotificationItems(selectedNotificationType);
	}

	private filterNotificationItems(selectedNotificationType: string): void {
		this.showBulkReview = false;
		switch (this.selectedNotificationFilterType) {
			case NotificationFilterTypes.ANNOUNCEMENTS:
				this.notificationItems = this.originalNotificationItems.filter(
					(notificationItem) =>
						notificationItem.eventType === NotificationFilterTypes.ANNOUNCEMENTS
				);
				break;
			case NotificationFilterTypes.COMMENTS:
				this.notificationItems = this.originalNotificationItems.filter(
					(notificationItem) =>
						notificationItem.eventType ===
							NOTIFICATION_EVENT_TYPE.APPEAL_COMMENT ||
						notificationItem.eventType ===
							NOTIFICATION_EVENT_TYPE.APPLICATION_COMMENT
				);
				break;
			case NotificationFilterTypes.STATUS_CHANGES:
				this.notificationItems = this.originalNotificationItems.filter(
					(notificationItem) =>
						notificationItem.eventType ===
							NOTIFICATION_EVENT_TYPE.APPEAL_STATUS_CHANGE ||
						notificationItem.eventType ===
							NOTIFICATION_EVENT_TYPE.APPLICATION_STATUS_CHANGE
				);
				break;
			case NotificationFilterTypes.FOR_REVIEW:
				this.setShowBulkReview();
				this.notificationItems = [];
				break;
			case NotificationFilterTypes.SYSTEM_GENERATED_EMAIL:
				this.notificationItems = this.originalNotificationItems.filter(
					(notificationItem) =>
						notificationItem.eventType === selectedNotificationType
				);
				break;
			case NotificationFilterTypes.UNREAD:
				this.notificationItems = this.originalNotificationItems.filter(
					(notificationItem) =>
						(notificationItem.type === NOTIFICATION_ITEM_TYPE.NOTIFICATION &&
							!this.notifications[notificationItem.index].read) ||
						(notificationItem.type === NOTIFICATION_ITEM_TYPE.ANNOUNCEMENT &&
							!this.announcementStatusMap.get(notificationItem.id)?.read)
				);
				this.setShowBulkReview();
				break;
			default:
				this.notificationItems = this.cloneNotificationItems(
					this.originalNotificationItems
				);
				this.setShowBulkReview();
				break;
		}
		this.hasNotifications = this.notificationItems?.length > 0;
	}

	private setShowBulkReview(): void {
		if (this.bulkAppReviewCount) {
			this.showBulkReview = true;
		}
	}

	private populateNotificationTypeOptions(): void {
		this.notificationTypeOptions = Object.keys(NotificationFilterTypes).map(
			(notificationFilterType) => ({
				display: NotificationFilterTypes[notificationFilterType],
				value: notificationFilterType,
				icon: NOTIFICATION_ICON_NAMES[notificationFilterType]
			})
		);
		if (!(this.isAdminAndSupervisor || this.isSupervisor)) {
			this.notificationTypeOptions = this.notificationTypeOptions.filter(
				(notificationTypeOption) =>
					notificationTypeOption.display !== NotificationFilterTypes.FOR_REVIEW
			);
		}

		this.notificationTypeOptions = SortUtilityService.sort(
			this.notificationTypeOptions,
			false,
			'display'
		);
		this.notificationTypeValue = EnumUtilityService.getEnumKeyByEnumValue(
			NotificationFilterTypes,
			NotificationFilterTypes.ALL_TYPES
		);
		this.selectedNotificationFilterType = NotificationFilterTypes.ALL_TYPES;
	}

	private readonly cloneNotificationItems = (
		notificationItems: Array<NotificationItem>
	): Array<NotificationItem> =>
		notificationItems.map((notificationItem) => notificationItem);

	private markAllNotificationAsRead(): void {
		if (
			this.selectedNotificationFilterType !==
				NotificationFilterTypes.FOR_REVIEW &&
			this.selectedNotificationFilterType !==
				NotificationFilterTypes.ANNOUNCEMENTS
		) {
			let index = -1;
			if (!this.notificationTypeValue) {
				this.notificationTypeValue = EnumUtilityService.getEnumKeyByEnumValue(
					NotificationFilterTypes,
					NotificationFilterTypes.ALL_TYPES
				);
			}

			this.notificationService
				.markAllNotificationsAsRead(
					this.participantId,
					this.notificationTypeValue
				)
				.subscribe((notifications) => {
					notifications.forEach((notification) => {
						index = this.notificationsMap.get(notification.id);
						if (index >= 0) {
							this.notifications[index].read = notification.read;
						}
						index = -1;
					});
				});
		}
	}

	private readonly getNotificationIdsToUpdate = () =>
		this.notificationItems
			.filter(
				(notificationItem) =>
					notificationItem.type === NOTIFICATION_ITEM_TYPE.NOTIFICATION &&
					!this.notifications[notificationItem.index]?.read
			)
			.map((notificationItem) => this.notifications[notificationItem.index].id);

	private mergeAnnouncementsAndNotifications(): void {
		let announcementItems: Array<NotificationItem> = [];
		announcementItems = this.announcementContents.map((announcement, index) =>
			this.buildNotificationItem(
				NOTIFICATION_ITEM_TYPE.ANNOUNCEMENT,
				announcement.id,
				announcement.startDate,
				NotificationFilterTypes.ANNOUNCEMENTS,
				index
			)
		);
		const notificationItemList = this.notifications.map(
			(notification, notificationIndex) =>
				this.buildNotificationItem(
					NOTIFICATION_ITEM_TYPE.NOTIFICATION,
					notification.id,
					Number(notification.eventDate),
					notification.eventType,
					notificationIndex
				)
		);
		this.originalNotificationItems = LodashUtilityService.uniqBy(
			[...announcementItems, ...notificationItemList],
			'id'
		);
		this.originalNotificationItems = SortUtilityService.sort(
			this.originalNotificationItems,
			true,
			'eventDate'
		);
		this.filterNotificationItems(
			EnumUtilityService.getEnumKeyByEnumValue(
				NotificationFilterTypes,
				this.selectedNotificationFilterType
			)
		);
	}

	private readonly buildNotificationItem = (
		itemType: NOTIFICATION_ITEM_TYPE,
		itemId: string,
		itemEventDate: number,
		notificationEventType: string,
		itemIndex: number
	): NotificationItem => ({
		type: itemType,
		id: itemId,
		eventDate: itemEventDate,
		eventType: notificationEventType,
		index: itemIndex
	});

	private updateAnnouncementStatusMapAndNotify(
		announcementStatus: AnnouncementStatus
	): AnnouncementStatus {
		announcementStatus.read = true;
		this.announcementStatusMap.set(
			announcementStatus.announcementId,
			announcementStatus
		);
		this.announcementUnReadCount =
			this.announcementUnReadCount - 1 >= 0 ?
				this.announcementUnReadCount--
			:	0;
		this.notifyAnnouncementAndAdminMsgCount();

		return announcementStatus;
	}

	private updateAnnouncementStatuses(
		announcementsStatusesList: Array<AnnouncementStatus>
	): void {
		this.notificationService
			.updateAnnouncementsStatuses(
				this.participantId,
				announcementsStatusesList
			)
			.subscribe((announcementsStatuses) =>
				announcementsStatuses.forEach((announcementStatus) =>
					this.announcementStatusMap.set(
						announcementStatus.announcementId,
						announcementStatus
					)
				)
			);
	}

	private markAllAnnouncementsAsRead(): void {
		if (
			this.selectedNotificationFilterType !==
				NotificationFilterTypes.FOR_REVIEW &&
			(this.selectedNotificationFilterType ===
				NotificationFilterTypes.ALL_TYPES ||
				this.selectedNotificationFilterType ===
					NotificationFilterTypes.ANNOUNCEMENTS ||
				this.selectedNotificationFilterType === NotificationFilterTypes.UNREAD)
		) {
			const announcementStatusList: Array<AnnouncementStatus> = [];
			this.notificationItems.forEach((notificationItem, index) => {
				if (
					!this.isAdminNotification &&
					notificationItem.type === NOTIFICATION_ITEM_TYPE.ANNOUNCEMENT &&
					!this.announcementStatusMap.get(notificationItem.id)?.read
				) {
					announcementStatusList.push(
						this.updateAnnouncementStatusMapAndNotify(
							this.announcementStatusMap.get(notificationItem.id)
						)
					);
				}
			});
			if (announcementStatusList?.length) {
				this.updateAnnouncementStatuses(announcementStatusList);
			}
		}
	}

	private notifyAnnouncementAndAdminMsgCount(): void {
		const announcementAndAdminMsgCount =
			this.announcementUnReadCount + (this.bulkAppReviewCount ? 1 : 0);
		this.notificationAlertIndicatorService.notifyAnnouncementAndAdminMsgCount(
			announcementAndAdminMsgCount
		);
	}

	private extractAnnouncementContent(announcements: Array<ContentItem>): void {
		if (announcements) {
			const announcementContents =
				GeneralContentService.mapContent(announcements);
			Object.keys(announcementContents).forEach((contentKey) => {
				const contents = announcementContents[contentKey];
				if (contents.length > 1) {
					// say more than one participant announcement
					this.announcementContents.push(...contents);
				} else {
					this.announcementContents.push(contents);
				}
			});

			this.announcementContents = this.announcementContents.filter(
				(announcement) =>
					announcement.startDate < this.participantService.getEODToday()
			);

			this.announcementContents = SortUtilityService.sort(
				this.announcementContents,
				true,
				'startDate'
			);
			this.isAnnouncementsRead();
		}
	}

	private readonly setEventsFlag = (notification: Notification) => {
		notification.isApplicationStatusChanged =
			notification.eventType ===
			NOTIFICATION_EVENT_TYPE.APPLICATION_STATUS_CHANGE;
		notification.isAppealStatusChanged =
			notification.eventType === NOTIFICATION_EVENT_TYPE.APPEAL_STATUS_CHANGE;
		notification.isApplicationCommentChanged =
			notification.eventType === NOTIFICATION_EVENT_TYPE.APPLICATION_COMMENT;
		notification.isAppealCommentChanged =
			notification.eventType === NOTIFICATION_EVENT_TYPE.APPEAL_COMMENT;
		notification.isSystemGeneratedEmail =
			notification.eventType === NOTIFICATION_EVENT_TYPE.SYSTEM_GENERATED_EMAIL;

		return notification;
	};

	private isAnnouncementsRead(): void {
		let unReadCount = 0;
		this.announcementContents.forEach((announcement, index) => {
			let isRead = true;
			if (announcement.id && this.announcementStatusMap.get(announcement.id)) {
				isRead = this.announcementStatusMap.get(announcement.id)?.read;
			} else {
				isRead = false;
				this.announcementStatusMap.set(announcement.id, {
					clientId: this.clientId,
					participantId: this.participantId,
					announcementId: announcement.id,
					read: isRead
				});
			}

			if (
				!isRead &&
				((this.notificationAccess &&
					this.isNewAnnouncement(
						announcement.startDate,
						this.notificationAccess.notificationLastReadDate
					)) ||
					!this.notificationAccess)
			) {
				unReadCount++;
			}
		});

		this.announcementUnReadCount = unReadCount;
	}

	private isNewAnnouncement(
		announcementStartDate: number,
		notificationLastAccessDate: string
	): boolean {
		return (
			new Date(announcementStartDate).getTime() >
			new Date(this.notificationAccess.notificationLastReadDate).getTime()
		);
	}

	private initAnnouncementStatusMap(
		announcementStatuses: Array<AnnouncementStatus>
	): void {
		announcementStatuses?.forEach((announcementStatus) => {
			if (
				announcementStatus.announcementId &&
				!this.announcementStatusMap.get(announcementStatus.announcementId)
			) {
				this.announcementStatusMap.set(
					announcementStatus.announcementId,
					announcementStatus
				);
			}
		});
	}
}
